import { useEffect } from "react";

/**
 * Regra da aplicação (decisão do usuário, 03/10/2026): todo modal fecha com um
 * clique fora dele, no fundo escuro.
 *
 * Em vez de repetir o handler em cada um dos ~60 modais, um ouvinte global faz
 * isso: o clique que começa E termina no próprio fundo (não num filho) aciona o
 * botão de fechar do modal. Assim vale o mesmo comportamento do "×" — inclusive
 * não fechar quando ele está desabilitado (salvando).
 *
 * Fundo que já trata o próprio clique (onClick do React) é ignorado, para não
 * fechar duas vezes. Arrastar a seleção de texto de dentro para fora não fecha.
 */
const OVERLAYS = [
  ".dpModalOverlay",
  ".estoqueModalOverlay",
  ".admModalOverlay",
  ".ModalOverlay",
  ".EditSupervisorOverlay",
  ".rgbModalOverlay",
  ".ecoDialog__overlay",
  ".storePanel__overlay",
  ".qPanel__overlay",
  ".colabSheet__overlay",
].join(", ");

const CLOSE_BUTTONS = [
  ".dpModal__close",
  ".estoqueModal__close",
  ".admModal__close",
  ".EditSupervisorHeaderClose",
  ".rgbModal__close",
  ".vseDialog__close",
  ".colabSheet__close",
  "[data-modal-close]",
  '[aria-label="Fechar"]',
].join(", ");

/** O elemento tem onClick do React? (as props ficam na chave `__reactProps$…` do nó). */
function hasReactClickHandler(el: Element): boolean {
  const key = Object.keys(el).find((k) => k.startsWith("__reactProps$"));
  const props = key ? (el as unknown as Record<string, { onClick?: unknown } | undefined>)[key] : undefined;
  return typeof props?.onClick === "function";
}

export function useCloseModalsOnOutsideClick() {
  useEffect(() => {
    let pressedOn: EventTarget | null = null;

    const onPointerDown = (e: PointerEvent) => {
      pressedOn = e.target;
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement) || !target.matches(OVERLAYS)) return;
      if (pressedOn !== target) return;
      if (hasReactClickHandler(target)) return;
      const close = target.querySelector<HTMLButtonElement>(CLOSE_BUTTONS);
      if (close && !close.disabled) close.click();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick);
    };
  }, []);
}
